import { inject, Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { firstValueFrom, map, Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import {
  GetRsabtsBody,
  GetRsabtsCenterMapViewResponse,
  GetRsabtsMapViewResponse,
  GetRsabtsMaxIntensityParams,
  GetRsabtsMaxIntensityResponse,
  GetRsabtsMaxTemperatureParams,
  GetRsabtsMaxTemperatureResponse,
  GetRsabtsResponse,
  GetRsabtStatsFieldType,
  GetRsabtStatsParams,
  GetRsabtStatsResponse,
  PostRsabtsDisableAlarmsResponse,
} from '../models/rsabts.models';
import {
  Filter,
  GetRsabtChartParams,
  GetRsabtHarmonicsParams,
  GetRsabtRankingParams,
  GetTsabtRankingParams,
  OrderType,
} from '../models/common.models';
import {
  ERROR_CONTEXT_ALARMS,
  ERROR_CONTEXT_HEADER,
  GET_RSABT_CHART_ENDPOINT,
  GET_RSABT_HARMONICS_ENDPOINT,
  GET_RSABT_RANKING_ENDPOINT,
  GET_RSABT_STATS_ENDPOINT,
  GET_RSABTS_CENTER_MAP_VIEW_ENDPOINT,
  GET_RSABTS_MAP_VIEW_ENDPOINT,
  GET_RSABTS_MAX_INTENSITY_ENDPOINT,
  GET_RSABTS_MAX_TEMPERATURE_ENDPOINT,
  GET_TSABT_RANKING_ENDPOINT,
  RSABTS_ENDPOINT,
  RSBATS_ALARMS_DISABLE_ENDPOINT,
} from '../constants/endpoints.constants';
import { GetRsabtHarmonicsResponse } from '../models/harmonics.models';
import {
  GetCenterChartFieldType,
  GetCenterChartResponse,
  GetRsabtRankingPaginatedResponse,
  GetTsabtRankingPaginatedResponse,
  GetTsabtRankingResponse,
  MaxIntensityRankingRsabt,
  RsabtRankingItem,
  Transformer,
  TsabtRankingItem,
  TsabtRankingItemBackend,
} from '@data';
import { withSpinner } from '../../app/core/services/spinner.controller';

/**
 * DataRsabtsService provides methods for interacting with RSABTs data through the API.
 * This service handles operations such as fetching RSABTs with filters and disabling RSABTs alarms.
 */
@Injectable({ providedIn: 'root' })
export class DataRsabtsService {
  protected readonly apiService = inject(ApiService);

  // Filter constants
  private readonly UPDATED_AT_FILTER_NAME = 'updatedAt';
  private readonly HAS_RSABT_FILTER_NAME = 'hasRsabt';
  private readonly GREATER_THAN_OR_EQUAL_OPERATOR = 'greaterThanOrEqual';
  private readonly LESS_THAN_OR_EQUAL_OPERATOR = 'lessThanOrEqual';
  private readonly EQUALS_OPERATOR = 'equals';

  /**
   * Fetches a paginated list of RSABTs based on specified query parameters and filters.
   *
   * @param pageIndex - The current page index for pagination.
   * @param pageSize - The number of records to fetch per page.
   * @param orderBy - The field to order the results by.
   * @param order - The ordering direction (`asc` or `desc`).
   * @param query - A string to search within RSABTs.
   * @param hasRsabt - A boolean indicating whether to filter RSABTs by availability.
   * @param filters - An array of `Filter` objects for additional filtering.
   * @returns An `Observable` of `GetRsabtsResponse` containing the fetched RSABTs data.
   */
  public getRsabts(
    pageIndex: number,
    pageSize: number,
    orderBy: string,
    order: OrderType,
    query: string,
    hasRsabt: boolean,
    filters: Filter[],
  ): Observable<GetRsabtsResponse> {
    const params = new HttpParams().appendAll({
      pageIndex,
      pageSize,
      orderBy,
      order,
    });
    const body: GetRsabtsBody = this.getFiltersBody(query, hasRsabt, filters);
    return this.apiService
      .post<GetRsabtsResponse>(RSABTS_ENDPOINT, body, {
        params,
      })
      .pipe(withSpinner());
  }

  /**
   * Retrieves RSABT chart data based on specified parameters.
   *
   * @param params - An object containing chart parameters:
   *   - `fields`: An array of field names or a string to specify the chart fields.
   *   - Other parameters as per `GetRsabtChartParams`, excluding `fields`.
   * @returns An `Observable` of `GetCenterChartResponse` containing the chart data.
   */
  public getRsabtChart(
    params: Omit<GetRsabtChartParams, 'fields'> & {
      fields: GetCenterChartFieldType[] | string;
    },
  ): Observable<GetCenterChartResponse> {
    if (Array.isArray(params.fields)) {
      params.fields = params.fields.join(',');
    }
    const queryParams = new HttpParams().appendAll({ ...params });
    return this.apiService
      .get<GetCenterChartResponse>(GET_RSABT_CHART_ENDPOINT, queryParams)
      .pipe(withSpinner());
  }

  /**
   * Fetches harmonics chart data for RSABTs based on the specified parameters.
   *
   * @param params - An object of type `GetRsabtHarmonicsParams` containing the parameters for the harmonics request.
   * @returns An `Observable` of `GetRsabtHarmonicsResponse` with the harmonics chart data.
   */
  public getRsabtHarmonicsChart(
    params: GetRsabtHarmonicsParams,
  ): Observable<GetRsabtHarmonicsResponse> {
    const queryParams = new HttpParams().appendAll({ ...params });
    return this.apiService
      .get<GetRsabtHarmonicsResponse>(
        GET_RSABT_HARMONICS_ENDPOINT, // Same endpoint as stats, need to call this endpoint once, not twice (SABT-539)
        queryParams,
      )
      .pipe(withSpinner());
  }

  public getRsabtHarmonics(
    params: GetRsabtHarmonicsParams,
  ): Observable<GetRsabtHarmonicsResponse> {
    const queryParams = new HttpParams().appendAll({ ...params });
    return this.apiService
      .get<GetRsabtHarmonicsResponse>(GET_RSABT_HARMONICS_ENDPOINT, queryParams)
      .pipe(withSpinner());
  }

  /**
   * Retrieves statistical data for RSABTs based on the specified parameters.
   *
   * @param params - An object containing:
   *   - `fields`: An array of field types or a string specifying the stats fields.
   *   - Other parameters as per `GetRsabtStatsParams`, excluding `fields`.
   * @returns An `Observable` of `GetRsabtStatsResponse` containing the statistical data.
   */
  public getRsbatStats(
    params: Omit<GetRsabtStatsParams, 'fields'> & {
      fields: GetRsabtStatsFieldType[] | string;
    },
  ): Observable<GetRsabtStatsResponse> {
    if (Array.isArray(params.fields)) {
      params.fields = params.fields.join(',');
    }
    const queryParams = new HttpParams().appendAll({ ...params });
    return this.apiService
      .get<GetRsabtStatsResponse>(
        GET_RSABT_STATS_ENDPOINT, // Same endpoint as chart, need to call this endpoint once, not twice (SABT-539)
        queryParams,
      )
      .pipe(withSpinner());
  }

  /**
   * Retrieves harmonic statistics for RSABTs based on specified parameters.
   *
   * @param params - An object of type `GetRsabtHarmonicParams` containing the parameters for harmonic stats.
   * @returns An `Observable` of `GetRsabtHarmonicResponse` with the harmonic statistics data.
   */
  public getRsbatHarmonicStats(
    params: GetRsabtHarmonicsParams,
  ): Observable<GetRsabtHarmonicsResponse> {
    const queryParams = new HttpParams().appendAll({ ...params });
    return this.apiService
      .get<GetRsabtHarmonicsResponse>(GET_RSABT_HARMONICS_ENDPOINT, queryParams)
      .pipe(withSpinner());
  }

  /**
   * Retrieves ranking statistics for RSABTs based on specified parameters.
   *
   * @param params - An object of type `GetRsabtRankingParams` containing:
   *   - `distributorIds`: An array of distributor IDs to filter the RSABTs (handled by tenant interceptor).
   *   - `dateFrom`: The start date timestamp for the ranking data.
   *   - `dateTo`: The end date timestamp for the ranking data.
   *   - `limit`: Optional limit for the number of results (default: 10).
   * @returns An `Observable` of `GetRsabtRankingResponse` with the ranking statistics data mapped to MaxIntensityRankingRsabt format.
   */
  public getRsbatsRanking(
    params: GetRsabtRankingParams,
  ): Observable<{ ranking: MaxIntensityRankingRsabt[] }> {
    // Note: distributorIds are automatically added by the tenant interceptor
    const queryParams = new HttpParams().appendAll({
      dateFrom: params.dateFrom.toString(),
      dateTo: params.dateTo.toString(),
      limit: (params.limit || 10).toString(),
    });

    return this.apiService
      .get<GetRsabtRankingPaginatedResponse>(
        GET_RSABT_RANKING_ENDPOINT,
        queryParams,
      )
      .pipe(
        map((response: GetRsabtRankingPaginatedResponse) => ({
          ranking: response.data.map((item) =>
            this.mapRsabtRankingItemToMaxIntensityRanking(item),
          ),
        })),
      );
  }

  /**
   * Maps RsabtRankingItem from backend to MaxIntensityRankingRsabt format expected by components.
   *
   * @param item - The RsabtRankingItem from backend response
   * @returns MaxIntensityRankingRsabt formatted object
   */
  private mapRsabtRankingItemToMaxIntensityRanking(
    item: RsabtRankingItem,
  ): MaxIntensityRankingRsabt {
    return {
      intensity: item.intensity,
      intensityPercentage: item.percentageIntensity,
      intensityPhaseR: item.intensityR,
      intensityPhaseS: item.intensityS,
      intensityPhaseT: item.intensityT,
      tsabtId: item.tsabtId || '',
      rsabtId: item.rsabtId,
      vtnId: item.vtnId || '',
      rsabtName: item.transformerName,
      centerId: item.centerId,
      centerName: item.centerName,
      distributorId: item.distributorId,
      distributorR1: item.distributorR1,
      transformerId: item.transformerId,
      timestamp: item.updatedAt,
      intensityNominal: item.nominalIntensity,
      transformers: (Array(item.transformersAmount).fill(1) || []).map(
        (_, tIndex): Transformer => ({
          position: tIndex + 1,
          status: 'disabled',
        }),
      ),
    };
  }

  /**
   * Retrieves ranking statistics for TSABTs based on specified parameters.
   *
   * @param params - An object of type `GetTsabtRankingParams` containing:
   *   - `distributorIds`: An array of distributor IDs to filter the TSABTs (handled by tenant interceptor).
   *   - `dateFrom`: The start date timestamp for the ranking data.
   *   - `dateTo`: The end date timestamp for the ranking data.
   *   - `limit`: Optional limit for the number of results (default: 10).
   * @returns An `Observable` of `GetTsabtRankingResponse` with the ranking statistics data mapped from backend format.
   */
  public getTsbatsRanking(
    params: GetTsabtRankingParams,
  ): Observable<GetTsabtRankingResponse> {
    // Note: distributorIds are automatically added by the tenant interceptor
    const queryParams = new HttpParams().appendAll({
      dateFrom: params.dateFrom.toString(),
      dateTo: params.dateTo.toString(),
      limit: (params.limit || 10).toString(),
    });

    return this.apiService
      .get<GetTsabtRankingPaginatedResponse>(
        GET_TSABT_RANKING_ENDPOINT,
        queryParams,
      )
      .pipe(
        map((response: GetTsabtRankingPaginatedResponse) => ({
          ranking: response.data.map((item, index) =>
            this.mapTsabtRankingItemBackendToFrontend(item, index),
          ),
        })),
      );
  }

  /**
   * Maps TsabtRankingItemBackend from backend to TsabtRankingItem format expected by components.
   *
   * @param item - The TsabtRankingItemBackend from backend response
   * @param index - The index for ordering
   * @returns TsabtRankingItem formatted object
   */
  private mapTsabtRankingItemBackendToFrontend(
    item: TsabtRankingItemBackend,
    index: number,
  ): TsabtRankingItem {
    return {
      tsabtId: item.tsabtId,
      lineName: item.lineName,
      lineId: item.lineId,
      intensity: item.intensity,
      intensityNominal: item.nominalIntensity,
      intensityPercentage: item.percentageIntensity,
      intensityPhaseR: item.phase === 'R' ? item.intensity : 0,
      intensityPhaseS: item.phase === 'S' ? item.intensity : 0,
      intensityPhaseT: item.phase === 'T' ? item.intensity : 0,
      phase: item.phase,
      order: index + 1,
      rsabtId: item.rsabtId,
      centerId: item.centerId,
      centerName: item.centerName,
      distributorId: item.distributorId,
      distributorR1: item.distributorR1,
      timestamp: item.updatedAt,
      vtnId: item.vtnId,
    };
  }

  /**
   * Retrieves maximum intensity data for RSABTs based on specified parameters.
   *
   * @param params - An object of type `GetRsabtsMaxIntensityParams` containing:
   *   - `distributorIds`: An array of distributor IDs to filter the RSABTs (handled by tenant interceptor).
   *   - `dateFrom`: The start date timestamp for the intensity data.
   *   - `dateTo`: The end date timestamp for the intensity data.
   * @returns An `Observable` of `GetRsabtsMaxIntensityResponse` containing the maximum intensity data.
   */
  public getRsabtsMaxIntensity(
    params: GetRsabtsMaxIntensityParams,
  ): Observable<GetRsabtsMaxIntensityResponse> {
    // Note: distributorIds are automatically added by the tenant interceptor
    const queryParams = new HttpParams().appendAll({
      dateFrom: params.dateFrom.toString(),
      dateTo: params.dateTo.toString(),
    });

    return this.apiService.get<GetRsabtsMaxIntensityResponse>(
      GET_RSABTS_MAX_INTENSITY_ENDPOINT,
      queryParams,
    );
  }

  /**
   * Retrieves maximum temperature data for RSABTs based on specified parameters.
   *
   * @param params - An object of type `GetRsabtsMaxTemperatureParams` containing:
   *   - `distributorIds`: An array of distributor IDs to filter the RSABTs (handled by tenant interceptor).
   *   - `dateFrom`: The start date timestamp for the temperature data.
   *   - `dateTo`: The end date timestamp for the temperature data.
   * @returns An `Observable` of `GetRsabtsMaxTemperatureResponse` containing the maximum temperature data.
   */
  public getRsabtsMaxTemperature(
    params: GetRsabtsMaxTemperatureParams,
  ): Observable<GetRsabtsMaxTemperatureResponse> {
    // Note: distributorIds are automatically added by the tenant interceptor
    const queryParams = new HttpParams().appendAll({
      dateFrom: params.dateFrom.toString(),
      dateTo: params.dateTo.toString(),
    });

    return this.apiService.get<GetRsabtsMaxTemperatureResponse>(
      GET_RSABTS_MAX_TEMPERATURE_ENDPOINT,
      queryParams,
    );
  }

  /**
   * Disables alarms for RSABTs based on the provided query and filters.
   *
   * @param query - A string representing the query to filter the RSABTs for which alarms should be disabled.
   * @param hasRsabt - A boolean indicating whether the RSABTs are present.
   * @param filters - An array of `Filter` objects used to apply additional filtering criteria to the RSABTs.
   * @returns A `Promise` resolving when the alarms are successfully disabled.
   */
  public disableRsabtsAlarms(
    query: string,
    hasRsabt: boolean,
    filters: Filter[],
  ) {
    const body = this.getFiltersBody(query, hasRsabt, filters);
    return firstValueFrom(
      this.apiService
        .post<PostRsabtsDisableAlarmsResponse>(
          RSBATS_ALARMS_DISABLE_ENDPOINT,
          body,
          {
            headers: {
              [ERROR_CONTEXT_HEADER]: ERROR_CONTEXT_ALARMS,
            },
          },
        )
        .pipe(withSpinner()),
    );
  }

  /**
   * Constructs the request body for RSABTs queries by combining filters and the query string.
   *
   * @param query - A string used to filter RSABTs data.
   * @param hasRsabt - A boolean indicating whether to include the `hasRsabt` filter.
   * @param filters - An array of `Filter` objects representing additional criteria for filtering.
   * @returns A `GetRsabtsBody` object containing the filters and query for the request body.
   */
  private getFiltersBody(query: string, hasRsabt: boolean, filters: Filter[]) {
    let bodyFilters: Filter[] = [];
    // Process each filter, handling special cases like date ranges
    filters.forEach((filter) => {
      // Handle updatedAt date range filter specially
      if (
        filter.name === this.UPDATED_AT_FILTER_NAME &&
        Array.isArray(filter.value) &&
        filter.value.length === 2
      ) {
        // Split date range into two separate filters with timestamps in milliseconds
        bodyFilters.push({
          name: this.UPDATED_AT_FILTER_NAME,
          operator: this.GREATER_THAN_OR_EQUAL_OPERATOR,
          value: new Date(filter.value[0]).getTime(), // Start date in milliseconds
        });
        bodyFilters.push({
          name: this.UPDATED_AT_FILTER_NAME,
          operator: this.LESS_THAN_OR_EQUAL_OPERATOR,
          value: new Date(filter.value[1]).getTime(), // End date in milliseconds
        });
      } else {
        // Handle regular filters
        bodyFilters.push({
          ...filter,
          operator: filter?.operator || this.EQUALS_OPERATOR,
        });
      }
    });

    const hasRsabtFilter: Filter = {
      name: this.HAS_RSABT_FILTER_NAME,
      operator: this.EQUALS_OPERATOR,
      value: hasRsabt,
    };
    bodyFilters = [...bodyFilters, hasRsabtFilter];
    const body: GetRsabtsBody = { filters: bodyFilters, query };
    return body;
  }

  private generateRsabtFakeData(count: number) {
    // TODO: Remove this test data and fake function, only for fast testing prupose.
    // Not aligned with project standards
    const ITEMS_TO_TEST = this.getTestItems();
    return Array(count)
      .fill(null)
      .map((_, index) => ({
        intensity: 100 + index + 95 + index + 105 + index, // Vary intensity slightly for uniqueness
        intensityNominal: 700 + index * 10, // Vary intensity slightly for uniqueness
        intensityPercentage: 80,
        intensityPhaseR: 100 + index,
        intensityPhaseS: 95 + index,
        intensityPhaseT: 105 + index,
        order: index + 1,
        tsabtId: ITEMS_TO_TEST[index].lineId || `tsabt${987 + index}`,
        rsabtId: ITEMS_TO_TEST[index].transformerId || `rsabt${654 + index}`,
        transformerId:
          ITEMS_TO_TEST[index].transformerId || `transformer${654 + index}`,
        rsabtName:
          ITEMS_TO_TEST[index].transformerName || `RSABT Unit ${index + 1}`,
        centerId: ITEMS_TO_TEST[index].centerId || `center${202 + index}`,
        centerName:
          ITEMS_TO_TEST[index].centerName ||
          `Distribution Center ${String.fromCharCode(65 + index)}`, // A, B, C, etc.
        distributorId: `distributor${333 + index}`,
        distributorR1:
          ITEMS_TO_TEST[index].distributorR1 || `R1-${333 + index}`,
        transformers: Array(2)
          .fill(null)
          .map((_, tIndex) => ({
            position: tIndex + 1,
            status: 'disabled',
          })),
        timestamp: Date.now() + index * 1000, // Incremental timestamp for uniqueness
      }));
  }

  private generateTsabtFakeData(count: number) {
    const ITEMS_TO_TEST = this.getTestItems();
    return Array(count)
      .fill(null)
      .map((_, index) => ({
        tsabtId: ITEMS_TO_TEST[index].lineId || `tsabt${187 + index}`,
        lineName: ITEMS_TO_TEST[index].lineName || `Line ${index + 1}`,
        intensity: 100 + index + 95 + index + 105 + index, // Vary intensity slightly for uniqueness
        intensityNominal: 700 + index * 10, // Vary intensity slightly for uniqueness
        intensityPercentage: 80,
        intensityPhaseR: 100 + index,
        intensityPhaseS: 95 + index,
        intensityPhaseT: 105 + index,
        phase: this.getRandomPhase(),
        order: index + 1,
        rsabtId: ITEMS_TO_TEST[index].transformerId || `rsabt${654 + index}`,
        centerId: ITEMS_TO_TEST[index].centerId || `center${202 + index}`,
        centerName:
          ITEMS_TO_TEST[index].centerName ||
          `Distribution Center ${String.fromCharCode(65 + index)}`, // A, B, C, etc.
        distributorId: `distributor${333 + index}`,
        distributorR1:
          ITEMS_TO_TEST[index].distributorR1 || `R1-${333 + index}`,
        timestamp: Date.now() + index * 1000, // Incremental timestamp for uniqueness
      }));
  }

  private getRandomPhase(): 'R' | 'S' | 'T' {
    const phases: Array<'R' | 'S' | 'T'> = ['R', 'S', 'T'];
    const randomIndex = Math.floor(Math.random() * phases.length);
    return phases[randomIndex];
  }

  // TODO: Remove this test data and fake function, only for fast testing prupose.
  private getTestItems() {
    // Not aligned with project standards
    return [
      {
        lineId: '106.LAT.P100000151',
        lineName:
          'L.M.T. AÉREO-SUBTERRÁNEA ENTRE APOYO Nº1 PARAJE LOS BARREROS A C.T. PARTICULAR COOPERATIVA OLIVARERA VIRGEN DE LUNA',
        centerId: '233.CTR.6311090198',
        centerName: 'CT URB. SAN BARTOLOME (SECTOR ARI-SUNCOD-4C)',
        transformerId: '106.TRF.P100000052',
        transformerName:
          'Transformador 106-L1049_1 630kVA C.T. PARTICULAR COOPERATIVA OLIVARERA VIRGEN DE LUNA',
        distributorR1: 'R1-106',
      },
      {
        lineId: '061.LBT.L100000152',
        lineName:
          '10314- Atzeko Kalea 8-14-16-20-22-24-26-28-30-32-Garaje-Boga Aozaratza 6- Ultzegin 1-3-5-7-9 Zubibarri 1-3 Ramon Irizar 1-3 Arantzazuko A',
        centerId: '022.CTR.T100000151',
        centerName: 'EDIFICIO PONTEAREAS CIUDAD (RUA DOS CARBALLOS)',
        transformerId: '199.TRF.P510000013',
        transformerName:
          'Transformador 199-I1201752_1 100kVA 201752 CTP52MO GASOLINERA REPSOL COMPLEJO LEO',
        distributorR1: 'R1-061',
      },
      {
        lineId: '039.LBT.T100000057',
        lineName:
          'HLI450 S2 AVD. LOPEZ ASTRAY, C/CLARA CAMPOAMOR, C/ MARUXA MALLO Y C/MANUEL MURGUIA (PUNTAS PISCINA)',
        centerId: '264.CTR.SG000658577',
        centerName: '2CTP031 E.D.A.R COOPERATIVA SANTA MARTA VIRGEN',
        transformerId: '290.TRF.P201000021',
        transformerName:
          'Transformador 290-I1001_1 25kVA (IN407A 95/52-3 AT) CT, ZONA RECREATIVA A VEIGA',
        distributorR1: 'R1-039',
      },
      {
        lineId: '106.LAT.P100000506',
        lineName:
          'L.M.T. AÉREA ENTRE APOYO Nº 3 PARAJE GÓNGORA-LAS VIÑAS A C.T. PARAJE LA POSADILLA (CORTIJO LAS ALMAGRERAS)',
        centerId: '022.CTR.XX00000296',
        centerName: 'DELEGACIONES REUNIDAS NOVOFRI S.A. (PRIVADO)',
        transformerId: '106.TRF.P100000115',
        transformerName:
          'Transformador 106-I1112_1 50kVA C.T. PARAJE LA POSADILLA (CORTIJO LAS ALMAGRERAS)',
        distributorR1: 'R1-106',
      },
      {
        lineId: '061.LBT.L100000387',
        lineName:
          '30213- Aita Madina 7 (NODO)+Lizaur Kalea 6 + Kale Zaharra 1-3-7-9 (OBRA)-11-17-19-25 + Unib. Etorbidea 2-4',
        centerId: '022.CTR.XX00000345',
        centerName: 'TELEFONICA SERVICIOS MOVILES, S.A. (PRIVADO)',
        transformerId: '044.TRF.SG000647641',
        transformerName:
          'Transformador Transformador 03103_1 160kVA 03 103 GRAJERA I 160kVA 03 103 GRAJERA I',
        distributorR1: 'R1-061',
      },
      {
        lineId: '061.LBT.L100000393',
        lineName:
          '30219- Lizaur Nº.: 7-Obispo Otadui 1-5-7 + Larraña Auzoa 34-35-36-37-AL Kurtzekua-Kale Zaharra 57-76',
        centerId: '022.CTR.XX00000418',
        centerName: 'AQUAGEST, S.A. \\\\EST. BOMBEO\\\\ (PRIVADO)',
        transformerId: '199.TRF.AGA1000026',
        transformerName:
          'Transformador 199-I1201504_1 25kVA 201504 CTR04MO EL ROBLEDILLO 1 CAMINO DE CALA',
        distributorR1: 'R1-061',
      },
      {
        lineId: '278.LBT.PT10000003',
        lineName:
          '12 1-9 A PR AGINT 15 (AGINT 15 - A/P S. MAR. 2) Y  AGINT 13-7-9-3 Y S. MARIA PLAZA 1 A PRON PL ZAHARRA)',
        centerId: '022.CTR.XX00000427',
        centerName: 'AGUAS MONDARIZ FUENTE DEL VAL VIDRIO (PRIVADO)',
        transformerId: '106.TRF.P100000033',
        transformerName:
          'Transformador 106-L1030_2 500kVA C.T. PARTICULAR COOPERATIVA GANADERA SAN MIGUEL',
        distributorR1: 'R1-106',
      },
      {
        lineId: '039.LBT.T100000065',
        lineName:
          'HLI443 S1 C/ROSALIA DE CASTRO Nº 14 (ED. PILAR PORTAL 2) Y C/JOSEFA SUAREZ GESTO Nº 2 (ED. PILAR PORTAL 4)',
        centerId: '290.CTR.P201000008',
        centerName: '(IN407A 95/52-3 AT) CT, ZONA RECREATIVA A VEIGA',
        transformerId: '199.TRF.6304063098',
        transformerName:
          'Transformador 199-I1201725_151407 100kVA 201725 CTP25MO EL ALCORNOCAL AGRODELGA',
        distributorR1: 'R1-039',
      },
      {
        lineId: '039.LBT.T100000077',
        lineName:
          'HLI437 S1 C/ LINO GOMEZ MALLO Nº 4 (EDIF. AS TORRES I PORTAL 1) Y Nº 6 (EDIF. AS TORRES I PORTAL 2)',
        centerId: '309.CTR.SG000694225',
        centerName: 'CENTRO PROTECCION, MEDIDAS Y TRANSFORMACION CAÑADA HONDA',
        transformerId: '100.TRF.T901000030',
        transformerName:
          'Transformador 100-C2039_1 50kVA 039 CT ISF. CTRA TIRTEAFUERA SERV. AUXIL. (PART)',
        distributorR1: 'R1-039',
      },
    ];
  }

  /**
   * Fetches RSABTs map view data for displaying on a geographical map.
   *
   * @param params - Parameters for fetching map view data including filters.
   * @returns An `Observable` of `GetRsabtsMapViewResponse` containing geographical RSABTs data for map markers.
   */
  getRsabtsMapView(params: {
    query?: string;
    hasRsabt?: boolean;
    filters?: Filter[];
  }): Observable<GetRsabtsMapViewResponse> {
    // Use the same getFiltersBody method as getRsabts to ensure consistency
    const body = this.getFiltersBody(
      params.query || '',
      params.hasRsabt !== undefined ? params.hasRsabt : true,
      params.filters || [],
    );

    return this.apiService
      .post<GetRsabtsMapViewResponse>(GET_RSABTS_MAP_VIEW_ENDPOINT, body)
      .pipe(withSpinner());
  }

  /**
   * Fetches detailed map view data for a specific center.
   *
   * @param params - Parameters including centerId and standard filtering parameters.
   * @returns An `Observable` of `GetRsabtsCenterMapViewResponse` containing detailed center data for popover display.
   */
  getRsabtsCenterMapView(params: {
    centerId: string;
    query?: string;
    hasRsabt?: boolean;
    filters?: Filter[];
  }): Observable<GetRsabtsCenterMapViewResponse> {
    const endpoint = GET_RSABTS_CENTER_MAP_VIEW_ENDPOINT.replace(
      '{{centerId}}',
      params.centerId,
    );

    const body = this.getFiltersBody(
      params.query || '',
      params.hasRsabt || false,
      params.filters || [],
    );

    return this.apiService
      .post<GetRsabtsCenterMapViewResponse>(endpoint, body)
      .pipe(withSpinner());
  }
}
